import { PointAddResponse } from "@1games-ru/region-shared/dist/client/requests/point";
import { InteriorType } from "@1games-ru/region-shared/dist/enums/interior/interior";
import { InteriorEstateGarage } from "@1games-ru/region-shared/dist/types/interior/interior-b2";
import { Point as WorldPoint } from "@1games-ru/region-shared/dist/types/world/world";
import { Dispatch, SetStateAction, useCallback, useId, useState } from "react";
import { CheckIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import { Point } from "@/domains/createEntity/constants/point";
import getInitPoint from "@/domains/createEntity/helpers/getInitPoint";
import { pointToPoint } from "@/domains/createEntity/helpers/pointToPoint";
import toast from "@/domains/toasts/helpers/toast";
import useGetInteriors from "@/domains/createEntity/hooks/useGetInteriors";
import usePoint from "@/domains/createEntity/hooks/usePoint";
import type { InteriorFormProps } from "@/domains/createEntity/containers/forms/interior/CrateEntityInteriorFormContainer";
import CreateEntityColumn from "@/domains/createEntity/components/CreateEntityColumn";
import CreateEntityRow from "@/domains/createEntity/components/CreateEntityRow";
import CreateEntitySetLocation from "@/domains/createEntity/components/CreateEntitySetLocation";
import CreateEntitySlider from "@/domains/createEntity/components/CreateEntitySlider";

export default function CreateEntityInteriorEstateGarageFormContainer({
  onSubmit,
  onClear,
}: InteriorFormProps<InteriorEstateGarage>) {
  const id = useId();
  const { addPoint } = usePoint();
  const [uuid, setUuid] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [zone, setZone] = useState<number | null>(30);
  const [point, setPoint] = useState<PointAddResponse | null>(null);

  const [spawnPoint, setSpawnPoint] = useState<PointAddResponse | null>(null);
  const [streetTransitPoint, setStreetTransitPoint] = useState<PointAddResponse | null>(null);
  const [livingTransitPoint, setLivingTransitPoint] = useState<PointAddResponse | null>(null);
  const [vehiclesPoints, setVehiclesPoints] = useState<PointAddResponse[]>([]);

  const { getInteriors } = useGetInteriors(InteriorType.EstateGarage);

  const addVehicle = useCallback(async () => {
    const vehPoint = await addPoint(getInitPoint(Point.VehicleLocation, { id }));
    if (vehPoint) {
      setVehiclesPoints((points) => [...points, vehPoint]);
    }
  }, [addPoint, id]);

  const deleteVehicle = useCallback(async (pointToDelete: PointAddResponse) => {
    setVehiclesPoints((points) => points.filter((vehPoint) => vehPoint.id !== pointToDelete.id));
  }, []);

  const clearPoints = useCallback(() => {
    setZone(30);
    setUuid(null);
    setPoint(null);
    setName(null);
    setDescription(null);
    setSpawnPoint(null);
    setStreetTransitPoint(null);
    setLivingTransitPoint(null);
    setVehiclesPoints([]);
    onClear();
  }, [onClear]);

  const createAndSetPoint = useCallback(
    async (
      pointType: Point,
      setter: (point: PointAddResponse | null) => void,
      additionalProps?: Record<string, unknown>,
    ) => {
      const p = await addPoint({
        ...getInitPoint(pointType, { id }),
        ...additionalProps,
      });
      setter(p);
      return p;
    },
    [addPoint, id],
  );

  const createAndSetPointArray = useCallback(
    async (
      pointType: Point,
      setter: Dispatch<SetStateAction<PointAddResponse[]>>,
      points: WorldPoint[],
    ) => {
      const promises = points.map(({ position }) =>
        addPoint({
          ...getInitPoint(pointType, { id }),
          position,
        }),
      );
      const p = await Promise.all(promises);
      setter(p);
      return p;
    },
    [addPoint, id],
  );

  async function handleSubmit() {
    if (
      !name ||
      !description ||
      !point ||
      !spawnPoint ||
      !streetTransitPoint ||
      !livingTransitPoint ||
      !vehiclesPoints.length
    ) {
      toast.error("Заполните все поля");
      return;
    }

    await onSubmit({
      uuid,
      name,
      description,
      content: {
        border: {
          range: zone,
          position: point?.position,
        },
        spawn: pointToPoint(spawnPoint),
        streetTransit: pointToPoint(streetTransitPoint),
        livingTransit: pointToPoint(livingTransitPoint),
        vehicles: vehiclesPoints.map((vehPoint) => pointToPoint(vehPoint)),
      },
    });
    clearPoints();
  }

  async function handleEdit() {
    const interiors = await getInteriors();
    const interior = interiors.find((item) => item.uuid === uuid);
    if (!interior) {
      toast.error("Гараж не найден");
      return;
    }

    const content = interior.content as InteriorEstateGarage;

    setName(interior.name);
    setDescription(interior.description);
    setZone(content.border.range);

    createAndSetPoint(Point.InteriorLocation, setPoint, {
      range: content.border.range,
      position: content.border.position,
    });
    createAndSetPoint(Point.Spawn, setSpawnPoint, content.spawn);
    createAndSetPoint(Point.StreetTransit, setStreetTransitPoint, content.streetTransit);
    createAndSetPoint(Point.LivingTransit, setLivingTransitPoint, content.livingTransit);

    createAndSetPointArray(Point.VehicleLocation, setVehiclesPoints, content.vehicles);
  }

  return (
    <>
      <Button variant="solid-outline" onClick={clearPoints}>
        Очистить точки
      </Button>

      <CreateEntityRow label="Редактировать гараж">
        <div className="flex gap-4">
          <TextInput
            placeholder="UUID гаража"
            value={uuid}
            onChange={(value) => setUuid(value)}
            className="w-full"
          />
          <Button onClick={() => handleEdit()} disabled={!uuid}>
            <CheckIcon size={16} />
          </Button>
        </div>
      </CreateEntityRow>

      <CreateEntityRow label="Название интерьера">
        <TextInput value={name} onChange={setName} placeholder="Придумайте название" />
      </CreateEntityRow>

      <CreateEntityRow label="Описание интерьера">
        <TextInput
          value={description}
          onChange={setDescription}
          placeholder="Придумайте описание"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Размер зоны интерьера">
        <CreateEntitySlider
          value={zone}
          min={10}
          max={50}
          step={10}
          values={[
            {
              label: "10",
              value: 10,
            },
            {
              label: "20",
              value: 20,
            },
            {
              label: "30",
              value: 30,
            },
            {
              label: "40",
              value: 40,
            },
            {
              label: "50",
              value: 50,
            },
          ]}
          onChange={setZone}
          className="px-1"
          classNameSlider="cursor-pointer"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение гаража">
        <CreateEntitySetLocation
          is={!!point}
          onChange={async () => {
            setPoint(await addPoint(getInitPoint(Point.InteriorLocation, { range: zone, id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение спауна игрока">
        <CreateEntitySetLocation
          is={!!spawnPoint}
          onChange={async () => {
            setSpawnPoint(await addPoint(getInitPoint(Point.Spawn, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение выхода на улицу">
        <CreateEntitySetLocation
          is={!!streetTransitPoint}
          onChange={async () => {
            setStreetTransitPoint(await addPoint(getInitPoint(Point.StreetTransit, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение входа в квартиру">
        <CreateEntitySetLocation
          is={!!livingTransitPoint}
          onChange={async () => {
            setLivingTransitPoint(await addPoint(getInitPoint(Point.LivingTransit, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение парковочных мест">
        <div className="flex flex-col gap-1.5">
          {vehiclesPoints.map((vehPoint, index) => (
            <CreateEntityColumn
              key={vehPoint.id}
              title="Парковочное место"
              onDelete={() => deleteVehicle(vehPoint)}
              index={index + 1}
            />
          ))}

          <Button
            data-mz-accent="success"
            className="w-full flex items-center justify-between h-[2.625rem] shrink-0 mt-1"
            onClick={addVehicle}
          >
            Добавить место
          </Button>
        </div>
      </CreateEntityRow>

      <Button onClick={() => handleSubmit()}>Сохранить интерьер</Button>
    </>
  );
}
